import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TextField } from "@material-ui/core";
import { AutocompleteComponent } from "components/Autocomplete/AutocompleteComponent";
import { getAvailableEquipment } from "redux/actions/adminUnits/adminUnitsActions";

export const EMPTY_EQUIPMENT_VALUES = {
  idEquipmentType: null, // dropdown "Equipo" (tipo)
  equipment: null, // dropdown "Número" (equipo concreto)
  code: "",
  serialNumber: "",
  status: "",
};

// Valores del formulario a partir de un equipo de /equipments/available
export const equipmentToValues = (eq) =>
  eq
    ? {
        idEquipmentType: {
          label: eq.typeName,
          value: eq.idEquipmentType,
          id: eq.idEquipmentType,
        },
        equipment: { ...eq, label: String(eq.number) },
        code: eq.code || "",
        serialNumber: eq.serialNumber || "",
        status: eq.status || "",
      }
    : EMPTY_EQUIPMENT_VALUES;

/**
 * Selección de equipo electrónico en dos pasos, compartida por
 * AssignEquipmentForm y DispenserForm: "Equipo" = tipo, "Número" = equipo
 * concreto (GET /equipments/available filtrado por tipo en el servidor).
 * Clave / No. serie / Estado son de solo consulta.
 *
 * Los tipos se leen de state.electronicEquipment.equipmentTypes; el formulario
 * padre se encarga de cargarlos con getElectronicEquipmentTypes.
 *
 * @param {object}   values        - { idEquipmentType, equipment, code, serialNumber, status }.
 * @param {function} onChange      - Recibe los valores que cambiaron.
 * @param {object}   station       - Planta para filtrar los equipos disponibles.
 * @param {number}   idCurrent     - Equipo asignado en backend; se incluye en la lista.
 * @param {object}   errors        - Errores de validación por campo.
 * @param {boolean}  lockEquipment - Impide dejar el campo sin equipo (no se puede quitar).
 */
export const EquipmentSelector = ({
  values,
  onChange,
  station,
  idCurrent,
  errors = {},
  lockEquipment = false,
}) => {
  const dispatch = useDispatch();
  const equipmentTypes = useSelector(
    (state) => state.electronicEquipment?.equipmentTypes || []
  );
  const [available, setAvailable] = useState([]);
  const [loadingNumbers, setLoadingNumbers] = useState(false);
  // Solo se usa la respuesta de la petición más reciente: si el tipo cambia
  // antes de que llegue una respuesta anterior, esa se descarta.
  const lastRequest = useRef(0);

  const { idEquipmentType, equipment, code, serialNumber, status } = values;
  const typeId = idEquipmentType?.value || idEquipmentType?.id;

  const typeOptions = useMemo(
    () =>
      equipmentTypes
        .map((t) => ({ ...t, label: t.name, value: t.id }))
        .sort((a, b) => (a.label || "").localeCompare(b.label || "", "es", { sensitivity: "base" })),
    [equipmentTypes]
  );

  useEffect(() => {
    const requestId = ++lastRequest.current;
    if (!typeId) {
      setAvailable([]);
      setLoadingNumbers(false);
      return;
    }
    const load = async () => {
      setLoadingNumbers(true);
      const list = await dispatch(
        getAvailableEquipment({
          stations: station?.id,
          idEquipmentType: typeId,
          idCurrent: idCurrent || undefined,
        })
      );
      if (requestId !== lastRequest.current) return;
      setAvailable(list || []);
      setLoadingNumbers(false);
    };
    load();
  }, [dispatch, typeId, station?.id, idCurrent]);

  const numberOptions = useMemo(
    () => available.map((eq) => ({ ...eq, label: String(eq.number) })),
    [available]
  );

  // Cambio de tipo → limpia el equipo seleccionado y los campos de consulta
  const handleTypeChange = ({ idEquipmentType: type }) => {
    onChange({ ...EMPTY_EQUIPMENT_VALUES, idEquipmentType: type });
  };

  // Selección de "Número" → llena Clave / No. serie / Estado
  const handleEquipmentChange = ({ equipment: selected }) => {
    onChange({
      equipment: selected,
      code: selected?.code || "",
      serialNumber: selected?.serialNumber || "",
      status: selected?.status || "",
    });
  };

  return (
    <>
      <AutocompleteComponent
        label="Equipo"
        name="idEquipmentType"
        value={idEquipmentType}
        handleValues={handleTypeChange}
        options={typeOptions}
        disableClearable={lockEquipment}
        textFieldProps={{
          error: !!errors.idEquipmentType,
          helperText: errors.idEquipmentType,
        }}
      />

      <AutocompleteComponent
        label="Número"
        name="equipment"
        value={equipment}
        handleValues={handleEquipmentChange}
        options={numberOptions}
        loading={loadingNumbers}
        disabled={!idEquipmentType}
        disableClearable={lockEquipment}
        textFieldProps={{
          error: !!errors.equipment,
          helperText: errors.equipment,
        }}
        sort
      />

      <TextField fullWidth label="Clave" value={code} variant="outlined" disabled />

      <TextField
        fullWidth
        label="No. serie"
        value={serialNumber}
        variant="outlined"
        disabled
      />

      <TextField fullWidth label="Estado" value={status} variant="outlined" disabled />
    </>
  );
};
